import { createClient, SupabaseClient } from '@supabase/supabase-js';
import { BehaviorSubject } from 'rxjs';

class SupabaseService {
  private static instance: SupabaseService;
  private client: SupabaseClient;
  private connectionStatus$ = new BehaviorSubject<'connected' | 'disconnected' | 'connecting'>('connecting');
  private retryCount = 0;
  private maxRetries = 3;
  private retryDelay = 2000;
  private heartbeatInterval: number | null = null;
  private readonly url: string;
  private readonly key: string;

  private constructor() {
    this.url = import.meta.env.VITE_SUPABASE_URL;
    this.key = import.meta.env.VITE_SUPABASE_ANON_KEY;
    
    if (!this.url || !this.key) {
      throw new Error('Supabase credentials not found');
    }

    this.client = createClient(this.url, this.key, {
      auth: {
        autoRefreshToken: true,
        persistSession: true,
        detectSessionInUrl: true,
        storageKey: 'suresign.auth.token',
        storage: window.localStorage
      },
      db: {
        schema: 'public'
      },
      realtime: {
        params: {
          eventsPerSecond: 10
        }
      }
    });

    this.initializeConnection();
  }

  public static getInstance(): SupabaseService {
    if (!SupabaseService.instance) {
      SupabaseService.instance = new SupabaseService();
    }
    return SupabaseService.instance;
  }

  private async initializeConnection() {
    try {
      this.connectionStatus$.next('connecting');
      
      // Test connection with a simple query instead of RPC
      const { error } = await this.client
        .from('profiles')
        .select('count', { count: 'exact', head: true });
      
      if (error) throw error;
      
      this.connectionStatus$.next('connected');
      this.startHeartbeat();
      this.retryCount = 0;
    } catch (error) {
      console.error('Supabase connection error:', error);
      this.handleConnectionError();
    }
  }

  private handleConnectionError() {
    this.connectionStatus$.next('disconnected');
    this.stopHeartbeat();

    if (this.retryCount < this.maxRetries) {
      this.retryCount++;
      console.log(`Retrying connection (${this.retryCount}/${this.maxRetries})...`);
      setTimeout(() => this.initializeConnection(), this.retryDelay * this.retryCount);
    } else {
      console.error('Max connection retries reached');
    }
  }

  private startHeartbeat() {
    if (this.heartbeatInterval) return;

    this.heartbeatInterval = window.setInterval(async () => {
      try {
        // Use the same simple query for heartbeat
        const { error } = await this.client
          .from('profiles')
          .select('count', { count: 'exact', head: true });
          
        if (error) throw error;
      } catch (error) {
        this.handleConnectionError();
      }
    }, 30000); // Check every 30 seconds
  }

  private stopHeartbeat() {
    if (this.heartbeatInterval) {
      window.clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  public getClient(): SupabaseClient {
    return this.client;
  }

  public getConnectionStatus$() {
    return this.connectionStatus$.asObservable();
  }

  public getCurrentStatus() {
    return this.connectionStatus$.getValue();
  }

  public async reconnect() {
    this.retryCount = 0;
    await this.initializeConnection();
  }

  public destroy() {
    this.stopHeartbeat();
    this.connectionStatus$.complete();
  }
}

export const supabaseService = SupabaseService.getInstance();
export const supabase = supabaseService.getClient();